import { useState, useEffect } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "./firebase";

// Settings › Text FriSo (the section's body; App.js draws its title): link a mobile number so the user can text FriSo (functions: smsSendCode,
// smsConfirmCode, smsRemove, smsWebhook). The wording here must match frisoapp.com/text, which carriers
// reviewed: unticked consent box, Send code off until it's ticked, the code comes from Twilio Verify, and
// "LINK <code>" texted to FriSo's number is the backup. Mockup: "FriSo Text FriSo" canvas.
export const FRISO_NUMBER = "+19286837476";
const FRISO_NUMBER_SHOWN = "(928) 683-7476";

const showPhone = (e164) => (/^\+1\d{10}$/.test(e164 || "") ? `(${e164.slice(2, 5)}) ${e164.slice(5, 8)}-${e164.slice(8)}` : e164 || "");
// "?&body=" opens the messages app with the text filled in on both iPhone and Android
const smsHref = (body) => `sms:${FRISO_NUMBER}${body ? `?&body=${encodeURIComponent(body)}` : ""}`;
// "?textfriso=test": a box that answers like a real text (smsTry), for checking replies before carriers approve
const TEST_BOX = new URLSearchParams(window.location.search).get("textfriso") === "test";
const errText = (err) => err?.message?.replace(/^.*?: /, "") || "Something went wrong. Please try again.";

export default function TextFriSo({ uid }) {
  const [link, setLink] = useState(undefined); // smsLinks/{uid}; null = none; undefined = loading
  const [step, setStep] = useState("form"); // "form" | "code" (while not linked)
  const [phone, setPhone] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(null); // { phone, linkCode, ok } from smsSendCode
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [tryText, setTryText] = useState("");
  const [tryLog, setTryLog] = useState([]); // [{ me, fs }] for the test box

  useEffect(() => onSnapshot(doc(db, "smsLinks", uid),
    (snap) => setLink(snap.exists() ? snap.data() : null),
    () => setLink(null)), [uid]);

  // Linked by the LINK text (or on another device): leave the code screen
  useEffect(() => {
    if (link?.phone) { setStep("form"); setSent(null); setCode(""); setError(""); }
  }, [link?.phone]);

  const call = async (name, data) => {
    setBusy(true); setError("");
    try { return (await httpsCallable(functions, name)(data)).data; }
    catch (err) { setError(errText(err)); return null; }
    finally { setBusy(false); }
  };

  const sendCode = async () => {
    if (!agreed || busy) return;
    const res = await call("smsSendCode", { phone, agreed: true });
    if (!res) return;
    setSent(res); setCode(""); setStep("code");
  };

  const confirm = async () => {
    if (busy || code.replace(/\D/g, "").length !== 6) return;
    await call("smsConfirmCode", { code });
  };

  const remove = async () => {
    if (!window.confirm("Remove your number? FriSo won't reply to texts from it anymore.")) return;
    if (await call("smsRemove")) { setAgreed(false); setPhone(""); }
  };

  const tryIt = async () => {
    const text = tryText.trim();
    if (!text || busy) return;
    setTryText("");
    setTryLog((log) => [...log, { me: text, fs: "…" }]);
    const res = await call("smsTry", { text });
    setTryLog((log) => [...log.slice(0, -1), { me: text, fs: res?.reply || "(no reply)" }]);
  };

  const startOver = () => { setStep("form"); setSent(null); setCode(""); setAgreed(false); setError(""); };

  if (link === undefined) return <span className="settings__muted">Loading…</span>;

  if (link?.phone) return (
    <>
      <div className="settings__row">
        <span>Linked to <strong>{showPhone(link.phone)}</strong> ✓</span>
        <button className="btn btn--secondary btn--sm" disabled={busy} onClick={remove}>Remove</button>
      </div>
      <div className="tf-panel">
        <span className="settings__muted">FriSo's number</span>
        <strong className="tf-number">{FRISO_NUMBER_SHOWN}</strong>
        <div className="tf-actions">
          <a className="btn btn--primary btn--sm" href={smsHref()}>Text FriSo</a>
          <a className="btn btn--secondary btn--sm" href="/friso.vcf" download="FriSo.vcf">Save to contacts</a>
        </div>
        <span className="tf-small">Try “dinner w climbing crew fri 7pm” or “when is everyone free this weekend?”</span>
      </div>
      {TEST_BOX && (
        <div className="tf-test">
          <span className="tf-label">Test replies (not sent as texts)</span>
          {tryLog.map((m, i) => (
            <div key={i} className="tf-test__pair"><span className="tf-test__me">{m.me}</span><span className="tf-test__fs">{m.fs}</span></div>
          ))}
          <div className="tf-row">
            <input className="form-input" value={tryText} placeholder="dinner w climbing crew fri 7pm"
              onChange={(e) => setTryText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && tryIt()} />
            <button className="btn btn--primary btn--sm" disabled={busy || !tryText.trim()} onClick={tryIt}>Send</button>
          </div>
        </div>
      )}
      {error && <div className="tf-error" role="alert">{error}</div>}
    </>
  );

  if (step === "code" && sent) return (
    <>
      <div className="tf-form">
        {sent.ok
          ? <span>We texted a 6-digit code to <strong>{showPhone(sent.phone)}</strong>.</span>
          : <span>We couldn't send a code to <strong>{showPhone(sent.phone)}</strong> right now. Text FriSo the code below instead.</span>}
        {sent.ok && <>
          <label className="tf-label" htmlFor="tf-code">Code</label>
          <input id="tf-code" className="form-input tf-code" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} onKeyDown={(e) => e.key === "Enter" && confirm()} />
          <div className="tf-row">
            <button className="tf-link" onClick={startOver}>Use a different number</button>
            <button className="btn btn--primary btn--sm" disabled={busy || code.length !== 6} onClick={confirm}>{busy ? "Checking…" : "Confirm"}</button>
          </div>
        </>}
        {error && <div className="tf-error" role="alert">{error}</div>}
        <div className="tf-panel">
          <span className="tf-small">{sent.ok ? "Didn't get it? " : ""}Text <strong>LINK {sent.linkCode}</strong> to FriSo at {FRISO_NUMBER_SHOWN}. It works for 10 minutes.</span>
          <div className="tf-actions">
            <a className="btn btn--secondary btn--sm" href={smsHref(`LINK ${sent.linkCode}`)}>Text LINK {sent.linkCode}</a>
            {!sent.ok && <button className="tf-link" onClick={startOver}>Use a different number</button>}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <div className="tf-form">
        <span className="tf-intro">Add plans and ask when your pods are free by texting FriSo from your phone.</span>
        <label className="tf-label" htmlFor="tf-phone">Your mobile number</label>
        <input id="tf-phone" className="form-input" type="tel" inputMode="tel" autoComplete="tel" placeholder="(555) 555-0100"
          value={phone} onChange={(e) => setPhone(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendCode()} />
        <label className="tf-consent">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <span>
            <span className="tf-consent__main">By checking this box, you grant FriSo permission to text you at the number above. Msg &amp; data rates may apply.</span>
            <span className="tf-small">Msg frequency varies. Reply STOP to opt out, HELP for help. <a href="https://frisoapp.com/privacy" target="_blank" rel="noreferrer">Privacy</a> · <a href="https://frisoapp.com/terms" target="_blank" rel="noreferrer">Terms</a></span>
          </span>
        </label>
        {error && <div className="tf-error" role="alert">{error}</div>}
        <button className="btn btn--primary tf-send" disabled={!agreed || busy || !phone.trim()} onClick={sendCode}>{busy ? "Sending…" : "Send code"}</button>
      </div>
    </>
  );
}
